import * as React from 'react';
import {
	Box,
	Button,
	Chip,
	Link,
	MenuItem,
	Select,
	Skeleton,
	Stack,
	Tooltip,
	Typography,
} from '@mui/material';
import { ExportIcon, VideoCallIcon, EyeIcon, DotsIcon } from '../icons';
import AssignCouncilorModal from '../components/councilor/AssignCouncilorModal';
import { useState } from 'react';
import CommonFilterTab from '../components/common/CommonFilterTab';
import { useDispatch } from 'react-redux';
import { useNotification } from '../hooks/useNotification';
import { assignCounselorToReferral, downloadReferralList, fetchAllReferralList } from '../api/referralApi';
import { useSelector } from 'react-redux';
import { UsersIcon } from '../icons';
import AddReferralNotes from '../components/referral/AddReferralNotes';
import { defaultPaginationValues, filterTypeOptions, webLeadsStatusList } from '../utils/constant';
import { setFilterField } from '../containers/filter-container/reducer/filterSlice';
import { formatMultiQueryParams, jsonParserFormatter } from '../utils/helperFunction';
import CommonDataTable from '../components/common/CommonDataTable';


const ReferralManagement = () => {
	const dispatch = useDispatch()
	const filterSlice = useSelector(state => state.filter?.filters)
	const LOCATION_FILTER = `filters:${window.location.pathname}`
	const [rows, setRows] = React.useState([]);
	const [loading, setLoading] = React.useState(true);
	const [rowCount, setRowCount] = React.useState(0);
	const [assignOpen, setAssignOpen] = useState(false);
	const [notesOpen, setNotesOpen] = useState(false);
	const [selectedReferral, setSelectedReferral] = React.useState(null);
	const [searchFilter, setSearchFilter] = React.useState({})
	const [pageFilters, setPageFilters] = React.useState(defaultPaginationValues);
	const [isRender, setIsRender] = React.useState(false)
	const { showErrorNotification, showSuccessNotification } = useNotification()
	const [downloading, setDownloading] = React.useState(false)

	const handleFliterSliceChange = (values = {}, isReset = false) => {
		let isQuery = typeof values === "object" && Object.keys(values)?.length
		isQuery && dispatch(setFilterField({ pathname: LOCATION_FILTER, filters: JSON.stringify({ ...(isReset ? {} : pageFilters), ...defaultPaginationValues, ...values }) }))
	}

	const fetchDetails = () => {
		const multiFilter = formatMultiQueryParams(filterSlice[LOCATION_FILTER])
		fetchAllReferralList({ setRows, filterData: { ...searchFilter, ...multiFilter, isMultiFilter: true }, setRowCount, setLoading })
			.catch(error => {
				showErrorNotification(error?.message)
			})
	}


	// const fetchCounsilorList = () => {
	// 	fetchCouncilorList({ search: "" })
	// 		.then(res => {
	// 			dispatch(setCouncilorList(res?.data || []))
	// 			setLoading(false)
	// 		}).catch(error => {
	// 			showErrorNotification(error?.message)
	// 		})
	// }
	// React.useEffect(() => {
	// 	fetchCounsilorList()
	// }, []);



	// Status colors mapping
	const statusStyles = {
		'Pending': {
			text: '#1075ff', // Dark green
			bg: 'rgba(16, 117, 255, 0.1)',   // Light green
		},
		'Prospect for next session': {
			text: 'rgba(252, 186, 34, 1)', // Orange
			bg: 'rgba(252, 186, 34, 0.1)',   // Light orange
		},
		'Registration Done': {
			text: 'rgba(103, 202, 22, 1)', // Blue
			bg: 'rgba(103, 202, 22, 0.1)',   // Light blue
		},
		'Not Interested': {
			text: 'rgba(242, 72, 34, 1)', // Dark red
			bg: 'rgba(242, 72, 34, 0.04)',   // Light red
		},
		'Paid': {
			text: 'rgba(109, 224, 15, 1)', // Dark red
			bg: 'rgba(109, 224, 15, 0.08)',   // Light red
		},
		'Interested': { bg: 'rgba(250, 144, 23, 0.04)', text: 'rgba(250, 144, 23, 1)' },
		'Call back request': { bg: 'rgba(0, 204, 255, 0.05)', text: 'rgba(27, 209, 255, 1)' },
		'Not Eligible': { bg: 'rgba(247, 45, 112, 0.09)', text: 'rgba(247, 45, 112, 1)' },
	};

	const infoDetails = {
		_id: selectedReferral?.id,
		student: selectedReferral?.friendsName,
		status: selectedReferral?.status,
		leadId: selectedReferral?.referralID,
	}

	const downloadAdmissionDetails = () => {
		if (downloading) return
		const multiFilter = formatMultiQueryParams(filterSlice[LOCATION_FILTER])
		downloadReferralList({ setDownloading, filterData: { ...searchFilter, ...multiFilter, isMultiFilter: true, isDownload: true } })
			.then(res => {
				showSuccessNotification("Successfully downloaded")
			}).catch(error => {
				showErrorNotification(error?.message)
			})
	}

	const columns = [
		{
			field: 'sNo',
			headerName: 'S. No.',
			width: 60,
			sortable: false,
			filterable: false,
			disableColumnMenu: true,
			// renderCell: (params) => rows?.findIndex((r) => r.id === params.row.id) + 1,
		},
		{
			field: 'referralID',
			headerName: 'Referral Id',
			flex: 1,
			minWidth: 130,
			cellClassName: 'sticky-col-1',
			headerClassName: 'sticky-col-1',
			filterColumnName: "referralId",
			filterType: filterTypeOptions.string,
		},
		{
			field: 'referrerName',
			headerName: 'Referrer Name',
			flex: 2,
			minWidth: 200,
			filterColumnName: "referrer.fullName",
			filterType: filterTypeOptions.string,
			renderCell: (params) => {
				const { img, referrerName } = params.row;

				const getInitials = (fullName) => {
					if (!fullName) return '';
					const words = fullName.trim().split(' ').filter(Boolean);
					if (words.length === 1) return words[0][0]?.toUpperCase() || '';
					if (words.length === 2) return (words[0][0] + words[1][0]).toUpperCase();
					return (words[0][0] + words[1][0] + words[2][0]).toUpperCase();
				};

				const stringToColor = (str) => {
					let hash = 0;
					for (let i = 0; i < str.length; i++) {
						hash = str.charCodeAt(i) + ((hash << 5) - hash);
					}
					const c = (hash & 0x00ffffff).toString(16).toUpperCase();
					return '#' + '00000'.substring(0, 6 - c.length) + c;
				};

				const bgColor = stringToColor(referrerName || 'Course');

				return loading ? (
					<Skeleton width={150} />
				) : (
					<Box
						display="flex"
						alignItems="center"
						gap={1}
						sx={{ cursor: 'pointer' }}
					>
						{img ? (
							<Box
								component="img"
								src={img}
								alt="logo"
								sx={{
									width: 40,
									height: 40,
									padding: '5px',
									borderRadius: '50%',
									objectFit: 'contain',
									backgroundColor: '#fff',
									boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
									display: 'block',
									maxHeight: 60,
								}}
							/>
						) : (
							<Box
								sx={{
									minWidth: 35,
									minHeight: 35,
									maxWidth: 35,
									maxHeight: 35,
									borderRadius: '50%',
									backgroundColor: bgColor,
									color: '#fff',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									fontWeight: 500,
									fontSize: '10px',
									textTransform: 'uppercase',
									boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
								}}
							>
								{getInitials(referrerName)}
							</Box>
						)}
						{params.value}
					</Box>
				);
			},
		},
		{
			field: 'friendsName',
			headerName: 'Friend’s Name',
			flex: 2,
			minWidth: 200,
			filterColumnName: "fullName",
			filterType: filterTypeOptions.string,
			renderCell: (params) => {
				const { img, friendsName } = params.row;

				const getInitials = (fullName) => {
					if (!fullName) return '';
					const words = fullName.trim().split(' ').filter(Boolean);
					if (words.length === 1) return words[0][0]?.toUpperCase() || '';
					if (words.length === 2) return (words[0][0] + words[1][0]).toUpperCase();
					return (words[0][0] + words[1][0] + words[2][0]).toUpperCase();
				};

				const stringToColor = (str) => {
					let hash = 0;
					for (let i = 0; i < str.length; i++) {
						hash = str.charCodeAt(i) + ((hash << 5) - hash);
					}
					const c = (hash & 0x00ffffff).toString(16).toUpperCase();
					return '#' + '00000'.substring(0, 6 - c.length) + c;
				};

				const bgColor = stringToColor(friendsName || 'Course');

				return loading ? (
					<Skeleton width={150} />
				) : (
					<Box
						display="flex"
						alignItems="center"
						gap={1}
						sx={{ cursor: 'pointer' }}
					>
						{img ? (
							<Box
								component="img"
								src={img}
								alt="logo"
								sx={{
									width: 40,
									height: 40,
									padding: '5px',
									borderRadius: '50%',
									objectFit: 'contain',
									backgroundColor: '#fff',
									boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
									display: 'block',
									maxHeight: 60,
								}}
							/>
						) : (
							<Box
								sx={{
									minWidth: 35,
									minHeight: 35,
									maxWidth: 35,
									maxHeight: 35,
									borderRadius: '50%',
									backgroundColor: bgColor,
									color: '#fff',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									fontWeight: 500,
									fontSize: '10px',
									textTransform: 'uppercase',
									boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
								}}
							>
								{getInitials(friendsName)}
							</Box>
						)}
						{params.value}
					</Box>
				);
			},
		},
		{
			field: 'friendMobileNumber',
			headerName: 'Friend’s Mobile No.',
			flex: 1.2,
			minWidth: 160,
			cellClassName: 'fw-bold',
			filterColumnName: "phone",
			filterType: filterTypeOptions.string,
		},
		{
			field: 'email',
			headerName: 'Email',
			flex: 1.2,
			minWidth: 160,
			cellClassName: 'fw-bold',
			filterColumnName: "email",
			filterType: filterTypeOptions.string,
		},
		{
			field: 'course',
			headerName: 'Course',
			flex: 1,
			minWidth: 180,
			filterColumnName: "courseInterest",
			filterType: filterTypeOptions.string,
			renderCell: (params) => {
				const { course } = params.row;

				return loading ? (
					<Skeleton width={150} />
				) : (
					<Box display="flex" sx={{ height: '100%', flexDirection: 'column', justifyContent: 'center', }}>
						{/* University Name */}
						<Typography sx={{ fontSize: '13px !important', color: '#1075FF', fontWeight: 500 }}>
							{course}
						</Typography>
					</Box>
				);
			},
		},
		{
			field: 'dob',
			headerName: 'Date of Birth',
			flex: 1.2,
			minWidth: 160,
			cellClassName: 'fw-bold',
			filterColumnName: "dob",
			filterType: filterTypeOptions.dateRange,
		},
		{
			field: 'gender',
			headerName: 'Gender',
			flex: 1.2,
			minWidth: 160,
			cellClassName: 'fw-bold',
			filterColumnName: "gender",
			filterType: filterTypeOptions.string,
			renderCell: (params) => {
				const { gender } = params.row;
				return loading ? (
					<Skeleton width={150} />
				) : (
					<Box display="flex" sx={{ height: '100%', flexDirection: 'column', justifyContent: 'center', }}>
						<Typography sx={{ fontSize: '13px !important', textTransform: 'capitalize' }}>
							{gender}
						</Typography>
					</Box>
				);
			},
		},
		{
			field: 'state',
			headerName: 'State',
			flex: 1.2,
			minWidth: 160,
			cellClassName: 'fw-bold',
			filterColumnName: "state",
			filterType: filterTypeOptions.string,
		},
		{
			field: 'city',
			headerName: 'City',
			flex: 1.2,
			minWidth: 160,
			cellClassName: 'fw-bold',
			filterColumnName: "city",
			filterType: filterTypeOptions.string,
		},
		{
			field: 'referredOnDate',
			headerName: 'Referred On Date',
			flex: 2,
			minWidth: 260,
			filterColumnName: "createdAt",
			filterType: filterTypeOptions.dateRange,
			renderCell: (params) => {
				const { referredOnDate, course } = params.row;

				return loading ? (
					<Skeleton width={150} />
				) : (
					<Box display="flex" sx={{ height: '100%', flexDirection: 'column', justifyContent: 'center', }}>
						{/* University Name */}
						<Typography sx={{ fontSize: '13px !important', fontWeight: 500 }}>
							{referredOnDate}
						</Typography>
					</Box>
				);
			},
		},
		{
			field: 'assignee',
			headerName: 'Assignee',
			flex: 2,
			minWidth: 180,
			filterColumnName: "counselorId.firstName",
			filterType: filterTypeOptions.string,
			renderCell: (params) => {
				const { img, assignee } = params.row;


				const getInitials = (fullName) => {
					if (!fullName || fullName == "-") return '';
					const words = fullName.trim().split(' ').filter(Boolean);
					if (words.length === 1) return words[0][0]?.toUpperCase() || '';
					if (words.length === 2) return (words[0][0] + words[1][0]).toUpperCase();
					return (words[0][0] + words[1][0] + words[2][0]).toUpperCase();
				};

				const stringToColor = (str) => {
					let hash = 0;
					for (let i = 0; i < str.length; i++) {
						hash = str.charCodeAt(i) + ((hash << 5) - hash);
					}
					const c = (hash & 0x00ffffff).toString(16).toUpperCase();
					return '#' + '00000'.substring(0, 6 - c.length) + c;
				};

				const bgColor = stringToColor(assignee || 'Course');

				return loading ? (
					<Skeleton width={150} />
				) : (
					<Box
						display="flex"
						alignItems="center"
						gap={1}
						sx={{ cursor: 'pointer' }}
					>
						{img ? (
							<Box
								component="img"
								src={img}
								alt="logo"
								sx={{
									width: 40,
									height: 40,
									padding: '5px',
									borderRadius: '50%',
									objectFit: 'contain',
									backgroundColor: '#fff',
									boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
									display: 'block',
									maxHeight: 60,
								}}
							/>
						) : (
							getInitials(assignee) ? <Box
								sx={{
									minWidth: 35,
									minHeight: 35,
									maxWidth: 35,
									maxHeight: 35,
									borderRadius: '50%',
									backgroundColor: bgColor,
									color: '#fff',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									fontWeight: 500,
									fontSize: '10px',
									textTransform: 'uppercase',
									boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
								}}
							>
								{getInitials(assignee)}
							</Box> : <></>
						)}
						{params.value}
					</Box>
				);
			},
		},
		{
			field: 'status',
			headerName: 'Status',
			flex: 1,
			minWidth: 180,
			headerClassName: 'text-center',
			cellClassName: 'text-center',
			filterColumnName: "status",
			filterType: filterTypeOptions.selection,
			selection: webLeadsStatusList,
			renderCell: (params) =>
				loading ? (
					<Skeleton width={70} />
				) : (
					<Chip
						label={params.value}
						size="small"
						sx={{
							px: 2,
							py: 1,
							bgcolor: statusStyles[params.value]?.bg || '#eee',
							color: statusStyles[params.value]?.text || '#000',
							fontWeight: 500,
						}}
					/>
				),
		},
		{
			field: 'lastUpdatedOn',
			headerName: 'Last Updated On',
			flex: 1,
			minWidth: 130,
			cellClassName: "fw-bold",
			filterColumnName: "updatedAt",
			filterType: filterTypeOptions.dateRange,

		},
		{
			field: "notes",
			headerName: "Notes",
			flex: 1.2,
			minWidth: 160,
			cellClassName: '',
			filterColumnName: "notes",
			filterType: filterTypeOptions.string,
		},
		{
			field: 'action',
			headerName: 'Action',
			flex: 2,
			minWidth: 250,
			cellClassName: 'text-center',
			headerClassName: "text-center",
			filterable: false,
			renderCell: (params) => {
				return loading ? (
					<Skeleton width={150} />
				) : (
					<>
						{params?.row?.status?.toLowerCase() !== "registration done" ?
							<Stack direction="row" spacing={1} sx={{ justifyContent: 'start', height: '100%' }}>
								<Button onClick={() => { setNotesOpen(true); setSelectedReferral(params?.row) }}
									size="small"
									variant="outlined" sx={{
										border: 'none', textAlign: 'center', minWidth: 'auto', '& svg': {
											width: '21px', height: '21px', fontSize: '21px'
										}
									}}
									startIcon={<DotsIcon sx={{ color: '#363853', fontSize: '20px', width: '20px', height: '20px' }} />} >
								</Button>
								<Button onClick={() => { setAssignOpen(true); setSelectedReferral(params?.row) }}
									size="small"
									variant="outlined" sx={{
										border: 'none', textAlign: 'center', minWidth: 'auto', '& svg': {
											width: '18px', height: '16px', fontSize: '16px'
										}
									}}
									startIcon={<UsersIcon />} >
								</Button>
							</Stack>
							: <Box></Box>
						}

					</>
				);
			},
		},
	];

	React.useEffect(() => {
		fetchDetails()
	}, [filterSlice[LOCATION_FILTER], isRender])

	// handle extra filter tabs changes
	React.useEffect(() => {
		if (pageFilters?.page) {
			handleFliterSliceChange({ page: 0 })
		} else {
			setIsRender(!isRender)
		}
	}, [searchFilter])

	// parse page filters
	React.useEffect(() => {
		const values = jsonParserFormatter(filterSlice[LOCATION_FILTER])
		setPageFilters(values)
	}, [filterSlice[LOCATION_FILTER]])

	return (
		<>
			{/* Apply global styles to DataGrid dropdown menus */}
			{/* <GlobalStyles
				styles={{
					'.MuiDataGrid-menuList': {
						fontSize: '13px',
						padding: '4px 0',
					},
					'.MuiDataGrid-panelContent': {
						fontSize: '13px',
						padding: '4px 0',
					},
					'.MuiSelect-select': {
						fontSize: '13px',
						padding: '4px 0',
					},
					'.MuiList-root .MuiButtonBase-root': {
						fontSize: '13px',
					},
					'.MuiDataGrid-panelContent .MuiOutlinedInput-input': {
						fontSize: '13px',
					},
					'.MuiDataGrid-columnsManagement .MuiFormControlLabel-root .MuiFormControlLabel-label': {
						fontSize: '13px',
					},
					'.MuiDataGrid-menuList .MuiTypography-root': {
						fontSize: '13px',
					},
					'.MuiDataGrid-menuList .MuiMenuItem-root': {
						fontSize: '13px',
						padding: '6px 12px',
					},
					'.MuiDataGrid-menuList .MuiMenuItem-root:hover': {
						backgroundColor: '#f5f5f5',
					},
					'& .MuiDataGrid-columnHeaders': {
						position: 'sticky',
						top: 0,
						zIndex: 2,
						backgroundColor: '#fff',
					},
					'& .MuiDataGrid-cell.sticky-col-1': {
						position: 'sticky',
						left: 0,
						background: '#fff',
						zIndex: 2,
					},
					'& .MuiDataGrid-columnHeader.sticky-col-1': {
						position: 'sticky',
						left: 0,
						background: '#fff',
						zIndex: 3, // slightly higher than cell
					},
				}}
			/> */}

			<Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
				<div className="page-header" style={{ position: 'sticky', top: '4.5rem', zIndex: '9', padding: '10px 15px', borderBottom: '1px solid #E6E7E8', background: '#fff', marginBottom: '12px' }}>
					<div className="row align-items-center">
						<div className="col">
							<Typography sx={{ fontSize: '18px', fontWeight: '600', color: '#15243E', display: 'flex', alignItems: 'center', gap: 1, }}>
								{/* <Link href="/" className='d-flex'><BackArrowIcon /></Link> */}
								Referral Management </Typography>
						</div>
					</div>
					{/* End Col */}
				</div>


				{/* Filter Toolbar */}
				<CommonFilterTab type='referral-management' module='referral' searchPlaceholder='Search ID/Name/Mobile...' detailType='Referral Management' fetchList={setSearchFilter} showCount={rows?.length} rowCount={rowCount} isExportBtn={true} isExporting={downloading} handleExport={downloadAdmissionDetails} />

				{/* Data Table */}
				<CommonDataTable columns={columns} rows={rows} idKey="field" loading={loading} filterEnabled={true} rowCount={rowCount}
					pageFilters={pageFilters}
					handleFliterSliceChange={handleFliterSliceChange}
				/>
			</Box>
			{notesOpen && <AddReferralNotes
				refetch={fetchDetails}
				open={notesOpen}
				info={infoDetails}
				onClose={() => setNotesOpen(false)}
			/>}
			<AssignCouncilorModal
				refetch={fetchDetails}
				open={assignOpen}
				info={infoDetails}
				assignCouncilorToCall={assignCounselorToReferral}
				onClose={() => setAssignOpen(false)} />
		</>
	)
}

export default ReferralManagement
